import React, { useId, useRef } from "react";
import { LayoutGroup, motion } from "framer-motion";
import { indicatorTransition, usePrefersReducedMotion } from "./motion";

/**
 * The sliding thumb behind the selected segment or tab (DESIGN.md "Motion":
 * shared-layout indicator, 200 ms spring). Render it inside the selected
 * item only; framer-motion glides it between items that share `layoutId`.
 * Wrap the group in <LayoutGroup id={…}> so two controls never trade thumbs.
 */
export const SegmentThumb = ({ layoutId = "sg-seg-thumb" }) => {
  const reduced = usePrefersReducedMotion();
  return (
    <motion.span
      layoutId={layoutId}
      className="sg-seg-thumb"
      aria-hidden="true"
      transition={indicatorTransition(reduced)}
      // Keep the rounded corners true while the thumb scales between widths.
      style={{ borderRadius: "inherit" }}
    />
  );
};

/**
 * Segmented control: a sunken track and a raised thumb that slides to the
 * selected option. Radio-group semantics with roving arrow keys.
 *
 *   <Segmented label="View" value={view} onChange={setView}
 *     options={[{ value: "table", label: "Table" }, { value: "board", label: "Board" }]} />
 *
 * variant "underline" draws tabs with a sliding accent bar instead.
 */
export const Segmented = ({
  options,
  value,
  onChange,
  label,
  variant,
  size,
  className = "",
  ...props
}) => {
  const groupId = useId();
  const rootRef = useRef(null);

  const onKeyDown = (event) => {
    const keys = [
      "ArrowRight",
      "ArrowDown",
      "ArrowLeft",
      "ArrowUp",
      "Home",
      "End",
    ];
    if (!keys.includes(event.key)) return;
    const enabled = options.filter((opt) => !opt.disabled);
    if (!enabled.length) return;
    event.preventDefault();
    const index = enabled.findIndex((opt) => opt.value === value);
    let next = index;
    if (event.key === "ArrowRight" || event.key === "ArrowDown")
      next = (index + 1) % enabled.length;
    if (event.key === "ArrowLeft" || event.key === "ArrowUp")
      next = (index - 1 + enabled.length) % enabled.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = enabled.length - 1;
    const target = enabled[next];
    onChange?.(target.value);
    requestAnimationFrame(() => {
      const items = rootRef.current?.querySelectorAll('[role="radio"]') || [];
      [...items]
        .find((item) => item.dataset.value === String(target.value))
        ?.focus();
    });
  };

  return (
    <LayoutGroup id={groupId}>
      <div
        ref={rootRef}
        role="radiogroup"
        aria-label={label}
        className={`sg-seg ${className}`}
        data-variant={variant}
        data-size={size}
        onKeyDown={onKeyDown}
        {...props}
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              disabled={opt.disabled}
              data-value={opt.value}
              className="sg-seg-item"
              title={opt.title}
              onClick={() => onChange?.(opt.value)}
            >
              {active && <SegmentThumb />}
              {opt.icon}
              {opt.label !== undefined && (
                <span
                  className={
                    opt.icon && opt.hideLabelOnPhone
                      ? "hidden sm:inline"
                      : undefined
                  }
                >
                  {opt.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </LayoutGroup>
  );
};

export default Segmented;
